import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { RouterPath } from "../../assets/dictionary/RouterPath";
import { toast } from "sonner";
import { trackEvent } from "../../lib/matomo";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../components/ui/sheet";
import {
  Plus,
  BellRing,
  Trash2,
  MapPin,
  Tag,
  Building2,
  Landmark,
  CalendarClock,
  ChevronRight,
  Sparkles,
  Crown,
  BellRingIcon,
} from "lucide-react";
import NotificationService from "./NotificationService";
import { useUser } from "../../context/UserContext";

// Plan limits mapping
const PLAN_LIMITS = {
  Aucun: 0,
  Basique: 1,
  Standard: 5,
  Premium: 15,
  Pro: 20,
};

const PLAN_COLORS = {
  Aucun: "bg-muted text-muted-foreground dark:bg-muted/50 dark:text-muted-foreground",
  Basique: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  Standard: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  Premium: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  Pro: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
};

const PLAN_ICONS = {
  Aucun: null,
  Basique: null,
  Standard: null,
  Premium: <Sparkles className="w-3 h-3" />,
  Pro: <Crown className="w-3 h-3" />,
};

function PlanBadge({ plan }) {
  const limit = PLAN_LIMITS[plan] ?? "∞";
  const colorClass = PLAN_COLORS[plan] ?? PLAN_COLORS.Basique;
  const icon = PLAN_ICONS[plan];
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${colorClass}`}>
      {icon}
      {plan}
    </span>
  );
}

function QuotaBar({ used, total }) {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const remaining = Math.max(total - used, 0);
  const isFull = remaining === 0;

  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 rounded-full bg-border/50 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${isFull ? "bg-destructive" : "bg-primary"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-xs font-medium tabular-nums ${isFull ? "text-destructive" : "text-muted-foreground"}`}>
        {remaining} restante{remaining !== 1 ? "s" : ""}
      </span>
    </div>
  );
}

function NotificationCard({ notification, onClick, onDelete }) {
  const localities = notification.locality ? notification.locality.split(", ") : [];
  const callTypes = notification.type_of_call ? notification.type_of_call.split(", ") : [];
  const companyTypes = notification.type_of_company ? notification.type_of_company.split(", ") : [];
  const emitters = notification.emitter ? notification.emitter.split(", ") : [];

  return (
    <Card
      className="group relative glass-card cursor-pointer border border-border/40 hover:border-primary/30 hover:shadow-md transition-all duration-200 bg-card/80 backdrop-blur-sm overflow-hidden"
      onClick={onClick}
    >
      {/* Subtle left accent */}
      <div className="absolute inset-y-0 left-0 w-0.5 bg-primary/0 group-hover:bg-primary/60 transition-all duration-200 rounded-l-full" />

      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <BellRing className="w-4 h-4 text-primary shrink-0" />
              <h3 className="font-semibold text-foreground truncate">{notification.title}</h3>
            </div>
            {notification.description && (
              <p className="text-sm text-muted-foreground line-clamp-2 mb-3 ml-6">
                {notification.description}
              </p>
            )}

            <div className="ml-6 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
              {localities.length > 0 && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-primary/60" />
                  {localities.slice(0, 2).join(", ")}
                  {localities.length > 2 && ` +${localities.length - 2}`}
                </span>
              )}
              {callTypes.length > 0 && (
                <span className="flex items-center gap-1">
                  <Tag className="w-3 h-3 text-primary/60" />
                  {callTypes.slice(0, 2).join(", ")}
                  {callTypes.length > 2 && ` +${callTypes.length - 2}`}
                </span>
              )}
              {companyTypes.length > 0 && (
                <span className="flex items-center gap-1">
                  <Building2 className="w-3 h-3 text-primary/60" />
                  {companyTypes.slice(0, 1).join(", ")}
                  {companyTypes.length > 1 && ` +${companyTypes.length - 1}`}
                </span>
              )}
              {emitters.length > 0 && (
                <span className="flex items-center gap-1">
                  <Tag className="w-3 h-3 text-primary/60" />
                  {emitters.slice(0, 1).join(", ")}
                  {emitters.length > 1 && ` +${emitters.length - 1}`}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(notification);
              }}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
            <ChevronRight className="w-4 h-4 text-muted-foreground/50 group-hover:text-primary/60 transition-colors" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function NotificationDetail({ notification, onDelete }) {
  const localities = notification.locality ? notification.locality.split(", ") : [];
  const callTypes = notification.type_of_call ? notification.type_of_call.split(", ") : [];
  const companyTypes = notification.type_of_company ? notification.type_of_company.split(", ") : [];
  const emitters = notification.emitter ? notification.emitter.split(", ") : [];

  return (
    <div className="space-y-6">
      {/* Description */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Description</p>
        <p className="text-sm text-foreground bg-muted/40 rounded-lg p-4 leading-relaxed">
          {notification.description || <span className="italic text-muted-foreground">Aucune description</span>}
        </p>
      </div>

      {/* Localities */}
      {localities.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> Localités ciblées
          </p>
          <div className="flex flex-wrap gap-1.5">
            {localities.map((loc, i) => (
              <Badge key={i} variant="secondary" className="text-xs">{loc}</Badge>
            ))}
          </div>
        </div>
      )}

      {/* Call types */}
      {callTypes.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5" /> Types d'aides
          </p>
          <div className="flex flex-wrap gap-1.5">
            {callTypes.map((type, i) => (
              <Badge key={i} className="text-xs">{type}</Badge>
            ))}
          </div>
        </div>
      )}

      {/* Company types */}
      {companyTypes.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5" /> Organismes bénéficiaires
          </p>
          <div className="flex flex-wrap gap-1.5">
            {companyTypes.map((type, i) => (
              <Badge key={i} variant="outline" className="text-xs">{type}</Badge>
            ))}
          </div>
        </div>
      )}

      {/* Emitters */}
      {emitters.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
            <Landmark className="w-3.5 h-3.5" /> Émetteurs
          </p>
          <div className="flex flex-wrap gap-1.5">
            {emitters.map((emitter, i) => (
              <Badge key={i} variant="secondary" className="text-xs">{emitter}</Badge>
            ))}
          </div>
        </div>
      )}

      {/* Sent at */}
      {notification.sent_at && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
            <CalendarClock className="w-3.5 h-3.5" /> Dernier envoi
          </p>
          <p className="text-sm text-foreground">
            {new Date(notification.sent_at).toLocaleString("fr-FR", {
              dateStyle: "long",
              timeStyle: "short",
            })}
          </p>
        </div>
      )}

      {/* Delete */}
      <div className="pt-4 border-t border-border/40">
        <Button
          variant="destructive"
          className="w-full"
          onClick={() => onDelete(notification)}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Supprimer cette demande de veille
        </Button>
      </div>
    </div>
  );
}

export default function AlertsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const { user } = useUser();
  const navigate = useNavigate();

  const plan = user?.type_abonnement ?? "Aucun";
  const planLimit = PLAN_LIMITS[plan] ?? 0;
  const usedCount = notifications.length;

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await NotificationService.getNotification();
      setNotifications(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Impossible de charger les demandes de veille.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleSelect = (notif) => {
    try { trackEvent("notification", "selected"); } catch (_) { }
    setSelectedNotification(notif);
    setSheetOpen(true);
  };

  const handleDeleteRequest = (notif) => {
    try { trackEvent("notification", "delete_clicked"); } catch (_) { }
    setNotificationToDelete(notif);
  };

  const handleDeleteConfirm = async () => {
    if (!notificationToDelete) return;
    try {
      const res = await NotificationService.deleteNotification(notificationToDelete.id);
      if (res.data > 0) {
        setNotifications((prev) => prev.filter((n) => n.id !== notificationToDelete.id));
        if (selectedNotification?.id === notificationToDelete.id) {
          setSheetOpen(false);
          setSelectedNotification(null);
        }
        toast.success("Demande de veille supprimée.");
        try { trackEvent("notification", "deleted"); } catch (_) { }
      } else {
        toast.error("Vous ne pouvez pas supprimer cette demande.");
        try { trackEvent("notification", "delete_forbidden"); } catch (_) { }
      }
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la suppression.");
      try { trackEvent("notification", "delete_error"); } catch (_) { }
    } finally {
      setNotificationToDelete(null);
    }
  };

  const canCreate = usedCount < planLimit;

  return (
    <div className="min-h-screen app-page-gradient">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">

        {/* Header */}
        <div>
          <div className="flex items-center gap-2 mb-2 pt-10 md:pt-0 wrap-balance">
            <BellRingIcon className="hidden sm:flex items-center w-5 h-5 text-primary" />
            <h1 className="text-4xl font-bold text-foreground">
              Mes veilles <span className="text-primary">actives</span>
            </h1>
          </div>
          <p className="text-muted-foreground">
            Alertes automatiques intelligentes par email.
          </p>
        </div>

        {/* Plan + quota card */}
        <Card className="border-border/40 bg-card/60 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Plan actuel :</span>
                <PlanBadge plan={plan} />
              </div>
              <div className="flex-1 min-w-[160px]">
                <QuotaBar used={usedCount} total={planLimit} />
              </div>

              <Button
                variant={`${canCreate ? 'default' : 'destructive'}`}
                size="sm"
                onClick={canCreate ? () => navigate(RouterPath.CREATE_ALERT) : () => navigate(RouterPath.BILLING)}
                className="text-xs"
              >
                <Plus className="w-4 h-4" />
                {canCreate ? "Nouvelle veille" : "Mettre à niveau"}
              </Button>

            </div>
          </CardContent>
        </Card>

        {/* Content */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 rounded-xl bg-muted/50 animate-pulse" />
            ))}
          </div>
        ) : notifications.length === 0 ? (
          /* Empty state */
          <Card className="border-border/30 border-dashed bg-card/40">
            <CardContent className="py-16 flex flex-col items-center text-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <BellRing className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-1">Aucune veille active</h2>
                <p className="text-sm text-muted-foreground max-w-sm">
                  Notre système de veille automatisé vous alerte dès qu’une opportunité pertinente est publiée.
                </p>
              </div>
              <Button onClick={() => navigate(RouterPath.CREATE_ALERT)} className="mt-2">
                <Plus className="w-4 h-4 mr-2" />
                Créer ma première veille
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {notifications.slice().reverse().map((notif) => (
              <NotificationCard
                key={notif.id}
                notification={notif}
                onClick={() => handleSelect(notif)}
                onDelete={handleDeleteRequest}
              />
            ))}
          </div>
        )}
      </div>

      {/* Detail sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto text-foreground">
          {selectedNotification && (
            <>
              <SheetHeader className="mb-6">
                <div className="flex items-center gap-2">
                  <BellRing className="w-5 h-5 text-primary shrink-0" />
                  <SheetTitle className="text-lg leading-tight">{selectedNotification.title}</SheetTitle>
                </div>
                <SheetDescription>Détails de la demande de veille</SheetDescription>
              </SheetHeader>
              <NotificationDetail
                notification={selectedNotification}
                onDelete={(notif) => {
                  setSheetOpen(false);
                  handleDeleteRequest(notif);
                }}
              />
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete dialog */}
      <Dialog open={!!notificationToDelete} onOpenChange={() => setNotificationToDelete(null)}>
        <DialogContent className="sm:max-w-[400px] text-foreground">
          <DialogHeader>
            <DialogTitle>Supprimer la veille</DialogTitle>
            <DialogDescription>
              Voulez-vous vraiment supprimer{" "}
              <strong>« {notificationToDelete?.title} »</strong> ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotificationToDelete(null)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}